import { useState, useEffect } from "react";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileText, X, RotateCcw, RotateCw, Check } from "lucide-react";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export const PROCESS_SERVICE_DOC_TYPES = [
  "Summons", "Complaint / Petition", "Subpoena", "Subpoena Duces Tecum", "Order to Show Cause",
  "Notice of Hearing", "Motion", "Court Order", "Writ", "Eviction / Detainer Warrant",
  "Divorce / Family Petition", "Order of Protection", "Exhibits / Attachments", "Other",
];
export const NOTARY_DOC_TYPES = [
  "Note", "Deed of Trust / Mortgage", "Closing Disclosure", "Loan Estimate", "Deed", "Right to Cancel",
  "Occupancy Affidavit", "Title / Escrow Documents", "Power of Attorney", "Will", "Trust", "Other",
];
const ACCEPTED = ["pdf", "jpg", "jpeg", "png", "heic"];
const ext = (n) => (n.split(".").pop() || "").toLowerCase();
const isImageName = (n) => ["jpg", "jpeg", "png", "heic"].includes(ext(n));
const isPdfName = (n) => ext(n) === "pdf";

// docs: [{id, filename, isImage, isPdf, file, previewUrl, confirmed, docType, otherDesc}]
export function docsReady(docs) {
  return docs.every((d) =>
    ((!d.isImage && !d.isPdf) || d.confirmed) && !!d.docType && (d.docType !== "Other" || (d.otherDesc || "").trim()));
}
export function docsMeta(docs) {
  return docs.map((d) => ({ id: d.id, document_type: d.docType === "Other" ? `Other — ${(d.otherDesc || "").trim()}` : d.docType }));
}

// PDF page thumbnails with per-page rotate + one "all pages upright" confirm for the file.
function PdfRotator({ doc, onConfirm }) {
  const [pages, setPages] = useState([]);       // data URLs
  const [rot, setRot] = useState([]);           // per-page degrees clockwise
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const buf = await doc.file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
        const imgs = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 0.45 });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width; canvas.height = viewport.height;
          await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
          imgs.push(canvas.toDataURL("image/jpeg", 0.7));
        }
        if (!cancelled) { setPages(imgs); setRot(new Array(imgs.length).fill(0)); setLoading(false); }
      } catch (e) {
        if (!cancelled) { setFailed(true); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.id]);

  const spin = (i, delta) => setRot((cur) => cur.map((r, idx) => (idx === i ? (((r + delta) % 360) + 360) % 360 : r)));

  const confirm = async () => {
    setSaving(true);
    try {
      await api.post(`/files/${doc.id}/rotate`, { page_rotations: failed ? [] : rot });
      onConfirm();
      toast.success("All pages confirmed upright");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save rotation");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid={`pdf-rotate-panel-${doc.id}`} className="rounded-md border border-amber-200 bg-amber-50 p-2">
      {loading && <p className="py-4 text-center text-xs text-amber-700">Loading pages…</p>}
      {failed && !loading && (
        <p className="py-2 text-center text-xs text-amber-700">Couldn't preview pages. Confirm the document is upright to continue.</p>
      )}
      {!loading && !failed && (
        <div className="flex flex-wrap justify-center gap-3">
          {pages.map((src, i) => (
            <div key={i} data-testid={`pdf-page-${doc.id}-${i}`} className="flex flex-col items-center gap-1">
              <div className="flex h-[160px] w-[130px] items-center justify-center overflow-hidden rounded border border-slate-200 bg-white">
                <img src={src} alt={`Page ${i + 1}`} style={{ transform: `rotate(${rot[i]}deg)`, maxHeight: 150, maxWidth: 120 }} className="transition-transform" />
              </div>
              <span className="text-[11px] text-slate-500">Page {i + 1}</span>
              <div className="flex gap-1">
                <button type="button" data-testid={`pdf-rotate-left-${doc.id}-${i}`} onClick={() => spin(i, -90)} className="rounded border border-slate-300 bg-white p-1 text-slate-600 hover:bg-slate-100"><RotateCcw className="h-3.5 w-3.5" /></button>
                <button type="button" data-testid={`pdf-rotate-right-${doc.id}-${i}`} onClick={() => spin(i, 90)} className="rounded border border-slate-300 bg-white p-1 text-slate-600 hover:bg-slate-100"><RotateCw className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 flex justify-center">
        <Button type="button" size="sm" disabled={loading || saving} data-testid={`pdf-confirm-upright-${doc.id}`} onClick={confirm} className="bg-amber-600 text-white hover:bg-amber-700">
          <Check className="mr-1 h-3.5 w-3.5" /> All pages are upright
        </Button>
      </div>
      <p className="mt-1 text-center text-[11px] text-amber-700">Check that every page is upright, then tick "All pages are upright" to continue.</p>
    </div>
  );
}

export function DocUploadList({ docs, setDocs, docTypes, accent = "emerald" }) {
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (files.find((f) => !ACCEPTED.includes(ext(f.name)))) {
      toast.error("We accept PDF and image files. To save a Word document as a PDF: File > Save As > PDF.");
      return;
    }
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const img = isImageName(f.name), pdf = isPdfName(f.name);
        setDocs((d) => [...d, { id: data.id, filename: data.filename, isImage: img, isPdf: pdf,
          file: pdf ? f : null, previewUrl: img ? URL.createObjectURL(f) : null,
          rotation: 0, confirmed: !(img || pdf), docType: "", otherDesc: "" }]);
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const patch = (id, p) => setDocs((cur) => cur.map((d) => (d.id === id ? { ...d, ...p } : d)));
  const rotate = (id, delta) => setDocs((cur) => cur.map((d) => (d.id === id ? { ...d, rotation: (((d.rotation + delta) % 360) + 360) % 360, confirmed: false } : d)));

  const confirmUpright = async (d) => {
    try {
      await api.post(`/files/${d.id}/rotate`, { rotation: d.rotation });
      patch(d.id, { confirmed: true });
      toast.success("Page confirmed upright");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save rotation"); }
  };

  return (
    <div className="space-y-4">
      <label data-testid="upload-dropzone" className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 py-8 transition hover:border-${accent}-400 hover:bg-${accent}-50/40`}>
        <Upload className="h-7 w-7 text-slate-400" />
        <p className="mt-2 text-sm font-medium text-slate-700">{uploading ? "Uploading…" : "Upload documents"}</p>
        <p className="text-xs text-slate-400">Accepted: PDF, JPG, JPEG, PNG, HEIC — PDF is fastest</p>
        <input data-testid="upload-input" type="file" multiple className="hidden" onChange={upload} accept=".pdf,.jpg,.jpeg,.png,.heic" />
      </label>

      {docs.map((d) => (
        <div key={d.id} data-testid={`doc-row-${d.id}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm text-slate-700"><FileText className="h-4 w-4 text-slate-500" /> {d.filename}</span>
            <button data-testid={`remove-doc-${d.id}`} onClick={() => setDocs((cur) => cur.filter((x) => x.id !== d.id))} className="text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>
          </div>

          {d.isImage && !d.confirmed && (
            <div data-testid={`rotate-panel-${d.id}`} className="rounded-md border border-amber-200 bg-amber-50 p-2">
              <div className="flex justify-center">
                <img src={d.previewUrl} alt={d.filename} style={{ transform: `rotate(${d.rotation}deg)`, maxHeight: 180 }} className="rounded transition-transform" />
              </div>
              <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
                <Button type="button" size="sm" variant="outline" data-testid={`rotate-left-${d.id}`} onClick={() => rotate(d.id, -90)}><RotateCcw className="mr-1 h-3.5 w-3.5" /> Rotate left</Button>
                <Button type="button" size="sm" variant="outline" data-testid={`rotate-right-${d.id}`} onClick={() => rotate(d.id, 90)}><RotateCw className="mr-1 h-3.5 w-3.5" /> Rotate right</Button>
                <Button type="button" size="sm" data-testid={`confirm-upright-${d.id}`} onClick={() => confirmUpright(d)} className="bg-amber-600 text-white hover:bg-amber-700"><Check className="mr-1 h-3.5 w-3.5" /> This page is upright</Button>
              </div>
              <p data-testid={`upright-notice-${d.id}`} className="mt-1 text-center text-[11px] text-amber-700">Check that the page is upright, then tick "This page is upright" to continue.</p>
            </div>
          )}

          {d.isPdf && !d.confirmed && d.file && (
            <PdfRotator doc={d} onConfirm={() => patch(d.id, { confirmed: true })} />
          )}

          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <Select value={d.docType || undefined} onValueChange={(v) => patch(d.id, { docType: v })}>
                <SelectTrigger data-testid={`doc-type-${d.id}`}><SelectValue placeholder="Select document type *" /></SelectTrigger>
                <SelectContent>{docTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {d.docType === "Other" && (
              <Input data-testid={`doc-other-${d.id}`} value={d.otherDesc} onChange={(e) => patch(d.id, { otherDesc: e.target.value })} placeholder="Describe the document *" />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
